"use client"

import { useState, useEffect, useRef, memo, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material"
import {
  CreditCard as CreditCardIcon,
  AccountBalance as AccountBalanceIcon,
  ShowChart as ShowChartIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
} from "@mui/icons-material"
import { useAuth } from "../../contexts/AuthContext"
import { getCreditsByOrganizationPaginated, getCreditsSummary } from "../../services/credit.service"
import { useOrganization } from "../../contexts/OrganizationContext"
import InfiniteScroll from 'react-infinite-scroll-component'

// Isolated customer table component - only re-renders when data changes
const CustomerTable = memo(({ 
  customers, 
  loading, 
  isFetching, 
  showLoadMore, 
  hasMore, 
  onFetchNextPage, 
  onLoadMore,
  onViewCustomer,
  formatNumber,
  getCreditStatusColor
}) => {
  const SCROLLABLE_TABLE_ID = 'customer-credits-table-scrollable'

  if (customers.length === 0 && !loading) {
    return (
      <TableRow>
        <TableCell colSpan={5} align="center">
          <Typography variant="body1" sx={{ py: 2 }}>
            No customers found
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      {customers.map((customer) => (
        <TableRow key={customer._id} hover>
          <TableCell>
            <Typography variant="body1">{customer.name || "Unnamed Customer"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {customer.email || "No email"}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body1">{formatNumber(customer.availableCredits)}</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography variant="body1">{formatNumber(customer.totalUsage)}</Typography>
          </TableCell>
          <TableCell align="center">
            <Chip
              label={customer.availableCredits > 0 ? "Active" : "No Credits"}
              color={getCreditStatusColor(customer.availableCredits)}
              size="small"
            />
          </TableCell>
          <TableCell align="center">
            <Button variant="contained" size="small" onClick={() => onViewCustomer(customer._id)}>
              View Details
            </Button>
          </TableCell>
        </TableRow>
      ))}
      
      {/* Load More Section */}
      {isFetching && (
        <TableRow>
          <TableCell colSpan={5} align="center">
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          </TableCell>
        </TableRow>
      )}
      
      {showLoadMore && !isFetching && (
        <TableRow>
          <TableCell colSpan={5} align="center">
            <Box sx={{ textAlign: 'center', my: 2, color: 'text.secondary' }}>
              <b>No more customers to load.</b>
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={onLoadMore}
                  sx={{ borderRadius: 2 }}
                >
                  Load More
                </Button>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  )
})

const CustomerCreditsList = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalCredits: 0,
    totalUsed: 0,
  })
  // Pagination states
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [noMoreData, setNoMoreData] = useState(false)
  const [showLoadMore, setShowLoadMore] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const limit = 20
  const searchTimeoutRef = useRef(null)
  const { selectedOrganization } = useOrganization()
  
  // Smart cache for credit summary data to avoid duplicate API calls
  const creditSummaryCache = useRef(new Map())

  // Helper function to get credit summary with caching
  const getCreditSummaryWithCache = async (customerId, organizationId) => {
    const cacheKey = `${customerId}-${organizationId}`
    
    // Check if we already have this data in cache
    if (creditSummaryCache.current.has(cacheKey)) {
      console.log(`✅ Cache HIT: Using cached credit summary for customer ${customerId}`)
      return creditSummaryCache.current.get(cacheKey)
    }
    
    // If not in cache, fetch from API
    console.log(`🔄 Cache MISS: Fetching credit summary for customer ${customerId}`)
    try {
      const creditsResponse = await getCreditsSummary(customerId, organizationId)
      const creditData = {
        availableCredits: creditsResponse.data?.availableCredits || 0,
        totalUsage: creditsResponse.data?.totalUsage || 0,
      }
      
      // Store in cache with size limit (prevent memory issues)
      if (creditSummaryCache.current.size >= 100) {
        // Remove oldest entries (simple FIFO)
        const firstKey = creditSummaryCache.current.keys().next().value
        creditSummaryCache.current.delete(firstKey)
      }
      
      creditSummaryCache.current.set(cacheKey, creditData)
      console.log(`💾 Cached credit summary for customer ${customerId} (cache size: ${creditSummaryCache.current.size})`)
      return creditData
    } catch (err) {
      console.error(`❌ Failed to fetch credits for customer ${customerId}:`, err)
      const fallbackData = {
        availableCredits: 0,
        totalUsage: 0,
      }
      // Cache the fallback data to avoid retrying failed requests
      creditSummaryCache.current.set(cacheKey, fallbackData)
      return fallbackData
    }
  }

  const getOrganizationId = () => {
    // Primary: Use the user's organization ID
    let organizationId = user?.organizationId

    // Secondary: Use selected organization from context if available
    if (!organizationId && selectedOrganization?._id) {
      organizationId = selectedOrganization._id
    }

    // Fallback: Only use selected organization from localStorage if user is superadmin
    if (!organizationId && user?.role === "superadmin") {
      const selectedOrg = localStorage.getItem("currentOrganization")
      if (selectedOrg) {
        try {
          organizationId = JSON.parse(selectedOrg)._id
        } catch (e) {
          console.error("Failed to parse stored organization:", e)
        }
      }
    }

    return organizationId
  }

  const fetchCustomersPaginated = async (pageToFetch = 1, reset = false, query = "") => {
    setIsFetching(true)
    setLoading(true)
    try {
      const organizationId = getOrganizationId()
      
      if (!organizationId) {
        throw new Error(
          user?.role === "superadmin"
            ? "No organization selected. Please select an organization."
            : "User organization not found.",
        )
      }

      const params = { organizationId, page: pageToFetch, limit }
      if (query) {
        params.search = query
      }
      
      const response = await getCreditsByOrganizationPaginated(params)
      let newCustomers = response.data || []
      setTotalCount(response.count || 0)
      
      // Fetch credit summary for each customer using smart caching
      const customersWithCredits = await Promise.all(
        newCustomers.map(async (customer) => {
          const creditData = await getCreditSummaryWithCache(customer._id, organizationId)
          return {
            ...customer,
            availableCredits: creditData.availableCredits,
            totalUsage: creditData.totalUsage,
          }
        })
      )
      
      if (reset) {
        setCustomers(customersWithCredits)
        setNoMoreData(false)
        setShowLoadMore(false)
        // Calculate stats from first page
        calculateStats(customersWithCredits)
      } else {
        setCustomers((prev) => {
          const existingIds = prev.map((customer) => customer._id)
          const filteredNewCustomers = customersWithCredits.filter((customer) => !existingIds.includes(customer._id))
          return [...prev, ...filteredNewCustomers]
        })
      }
      
      if (newCustomers.length < limit) {
        setNoMoreData(true)
        setShowLoadMore(true)
      } else {
        setNoMoreData(false)
        setShowLoadMore(false)
      }
      setHasMore(!showLoadMore)
    } catch (error) {
      console.error("Failed to fetch customers:", error)
      setError(error.message || "Failed to load customer credits data")
    } finally {
      setLoading(false)
      setIsFetching(false)
    }
  }

  const calculateStats = (customersData) => {
    const totalCustomers = customersData.length
    const totalCredits = customersData.reduce(
      (sum, customer) => sum + (customer.availableCredits || 0),
      0,
    )
    const totalUsed = customersData.reduce(
      (sum, customer) => sum + (customer.totalUsage || 0),
      0,
    )

    setStats({
      totalCustomers,
      totalCredits,
      totalUsed,
    })
  }

  // Clear cache when organization changes
  useEffect(() => {
    creditSummaryCache.current.clear()
    console.log('Credit summary cache cleared due to organization change')
  }, [selectedOrganization?._id, user?.organizationId])

  useEffect(() => {
    if (getOrganizationId()) {
      setCustomers([])
      setPage(1)
      setHasMore(true)
      fetchCustomersPaginated(1, true)
    }
  }, [user, selectedOrganization])


  const fetchNextPage = useCallback(() => {
    if (isFetching || showLoadMore) return
    setPage((prevPage) => {
      let nextPage = prevPage
      if(totalCount >= limit) {
        nextPage = prevPage + 1
      }
      fetchCustomersPaginated(nextPage, false, searchQuery)
      return nextPage
    })
  }, [isFetching, showLoadMore, totalCount, limit, searchQuery])

  const handleLoadMore = useCallback(() => {
    setShowLoadMore(false)
    setPage((prevPage) => {
      let nextPage = prevPage
      if(totalCount >= limit) {
        nextPage = prevPage + 1
      }
      fetchCustomersPaginated(nextPage, false, searchQuery)
      return nextPage
    })
  }, [totalCount, limit, searchQuery])

  const handleSearch = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // Debounce the API call
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1)
      setCustomers([])
      setNoMoreData(false)
      setShowLoadMore(false)
      fetchCustomersPaginated(1, true, query)
    }, 500)
  }

  const formatNumber = (num) => {
    return num?.toLocaleString() || "0"
  }

  const getCreditStatusColor = (availableCredits) => {
    if (availableCredits <= 0) return "error"
    if (availableCredits < 100) return "warning"
    return "success"
  }

  const handleViewCustomer = (customerId) => {
    navigate(`/credits/customer/${customerId}`)
  }

  console.log("error", error)
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ bgcolor: "#FFF4F4", p: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <ErrorIcon color="error" sx={{ mr: 2 }} />
            <Typography variant="h5" component="h2">
              An error occurred
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            There was a problem loading the customer credits data.
          </Typography>
          <Box sx={{ bgcolor: "#FFEBEB", p: 2, borderRadius: 1, mb: 3 }}>
            <Typography variant="subtitle2" color="error">
              Error Details
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Box>
          <Button variant="contained" color="primary" onClick={() => {
            setError(null)
            setCustomers([])
            setPage(1)
            setHasMore(true)
            fetchCustomersPaginated(1, true, searchQuery)
          }} sx={{ borderRadius: 28 }}>
            Try Again
          </Button>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Customer Credits
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage credits for all customers
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search customers..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccountBalanceIcon sx={{ color: "primary.main", mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h5" component="div">
                    {formatNumber(stats.totalCustomers)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Customers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CreditCardIcon sx={{ color: "success.main", mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h5" component="div">
                    {formatNumber(stats.totalCredits)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available Credits
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ShowChartIcon sx={{ color: "info.main", mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h5" component="div">
                    {formatNumber(stats.totalUsed)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credits Used
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Customers Table */}
      <TableContainer component={Paper} sx={{ mb: 4, height: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell align="right">Available Credits</TableCell>
              <TableCell align="right">Credits Used</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
        </Table>
        <Box id="customer-credits-table-scrollable" sx={{ height: 600, overflow: 'auto' }}>
          <InfiniteScroll
            dataLength={customers.length}
            next={fetchNextPage}
            hasMore={!showLoadMore}
            scrollableTarget="customer-credits-table-scrollable"
            scrollThreshold={0.98}
          >
            <Table>
              <TableBody>
                <CustomerTable
                  customers={customers}
                  loading={loading}
                  isFetching={isFetching}
                  showLoadMore={showLoadMore}
                  hasMore={hasMore}
                  onFetchNextPage={fetchNextPage}
                  onLoadMore={handleLoadMore}
                  onViewCustomer={handleViewCustomer}
                  formatNumber={formatNumber}
                  getCreditStatusColor={getCreditStatusColor}
                />
              </TableBody>
            </Table>
          </InfiniteScroll>
        </Box>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button variant="outlined" color="primary" onClick={() => navigate("/credits")}>
          Back to Credits
        </Button>
      </Box>
    </Box>
  )
}

export default CustomerCreditsList

